import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import { LINKS } from "@/lib/data";
import { SHOP_ENABLED } from "@/lib/config";
import { scrollToId } from "@/components/Header";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/c26e8a3e-5883-4728-a91d-6b76ec3468ef/images/07cc4abee27d333c52d55c5c92ea221207c91a35756abef46820a459c6ad6674.jpeg";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.14 },
  }),
};

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505] flex items-end"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt="Monthly Pops pop-culture collectibles and autographed memorabilia display"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
      </motion.div>

      {/* Teal radial glow */}
      <div className="pointer-events-none absolute -top-40 -right-32 z-0 h-[520px] w-[520px] rounded-full bg-[#3FE1D1]/20 blur-[140px]" />

      {/* Faint kinetic brand mark */}
      <motion.img
        src="/brand/mp-mark.png"
        alt=""
        aria-hidden="true"
        initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
        animate={{ opacity: 0.06, scale: 1, rotate: 0 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
        className="pointer-events-none absolute z-10 top-[10%] right-[6%] w-[38vw] max-w-[420px] hidden md:block"
      />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-20 w-full max-w-[1440px] mx-auto px-6 md:px-12 pb-16 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-2 w-2 rounded-full bg-[#3FE1D1] animate-pulse" />
          <span className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-white/70 font-medium">
            The Premier Autograph Destination
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase leading-[0.86] tracking-tighter text-white text-[16vw] sm:text-[13vw] md:text-[9.5vw] lg:text-[8.5rem]">
          {["Where the", "grails go", "live."].map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 1 ? (
                  <>
                    <span className="text-[#3FE1D1]">grails</span> go
                  </>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end gap-8 md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="max-w-md text-white/70 text-base md:text-lg leading-relaxed"
          >
            Six years, 100,000+ items, and the longest-running auction on Whatnot.
            Catch us live, then send us your grails to consign.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            {SHOP_ENABLED ? (
              <button
                data-testid="hero-shop-cta"
                onClick={() => navigate("/shop")}
                className="group inline-flex items-center gap-2 bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-[0.14em] px-8 py-4 hover:bg-white transition-colors duration-200"
              >
                Shop High-End
                <ArrowUpRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            ) : (
              <button
                data-testid="hero-shop-cta"
                onClick={() => scrollToId("sell")}
                className="group inline-flex items-center gap-2 bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-[0.14em] px-8 py-4 hover:bg-white transition-colors duration-200"
              >
                Where We Sell
                <ArrowUpRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            )}
            <a
              data-testid="hero-whatnot-cta"
              href={LINKS.whatnot}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 border border-[#3FE1D1] text-[#3FE1D1] font-bold uppercase text-sm tracking-[0.14em] px-8 py-4 hover:bg-[#3FE1D1]/10 transition-colors duration-200"
            >
              <Play size={15} className="fill-[#3FE1D1]" />
              Watch Live
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
